import axios from 'axios';
import * as Sentry from '@sentry/react';
import { supabase } from '../lib/supabase';

// Returns true if the JWT is expired or expires within 60 seconds.
// Treats unparseable tokens as stale so refresh is always attempted.
function tokenIsStale(accessToken) {
  try {
    const payload = JSON.parse(atob(accessToken.split('.')[1]));
    return payload.exp - Date.now() / 1000 < 60;
  } catch {
    return true;
  }
}

const betClient = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL,
});

betClient.interceptors.request.use(async (config) => {
  let { data: { session } } = await supabase.auth.getSession();

  // Proactively refresh if the token is expired or expiring soon.
  // Only attempted when a session exists (signed-in users); skipped for
  // anonymous users so the free-tier / marketing funnel path is unchanged.
  if (session?.access_token && tokenIsStale(session.access_token)) {
    const { data: refreshed, error } = await supabase.auth.refreshSession();
    if (refreshed?.session) {
      session = refreshed.session;
    } else {
      // Had a session but refresh failed — this is the silent auth failure
      // that causes gated features to show free-tier content to paid users.
      Sentry.addBreadcrumb({
        category: 'auth',
        message: 'betClient: token refresh failed for signed-in user',
        data: { error: error?.message ?? 'unknown' },
        level: 'warning',
      });
      Sentry.captureMessage('betClient: auth token missing for signed-in user', {
        level: 'warning',
        tags: { auth_token_missing: 'true' },
      });
    }
  }

  if (session?.access_token) {
    config.headers.Authorization = `Bearer ${session.access_token}`;
  }
  return config;
});

export default betClient;
